import { useEffect } from "react"

import { useQuery } from "@tanstack/react-query"

import { supabase } from "../lib/supabase"
import { DateFilter } from "../types/filters"

export interface SourceData {
  name: string
  value: number
}

/**
 * Hook to fetch applications by source data from the metrics-applications-by-source endpoint
 * @param dateFilter Optional date range filter
 * @returns Query result with applications by source data
 */
export const useApplicationsBySourceData = (dateFilter?: DateFilter, jobId?: string) => {
  const query = useQuery<SourceData[]>({
    queryKey: ["applicationsBySource", dateFilter, jobId ?? "all"],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("metrics-applications-by-source", {
        body: {
          startDate: dateFilter?.startDate || null,
          endDate: dateFilter?.endDate || null,
          jobId: jobId ?? null,
        },
      })

      if (error) {
        throw new Error(`Error fetching applications by source data: ${error.message}`)
      }

      return data
    },
    // Keep data fresh for 5 minutes
    staleTime: 5 * 60 * 1000,
    // Enable refetch on window focus
    refetchOnWindowFocus: true,
  })

  // Effect to refetch data when component mounts/tab becomes active
  useEffect(() => {
    // Refetch data when component mounts
    query.refetch()

    // Set up event listener for visibility change
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        query.refetch()
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange)

    // Clean up event listener on unmount
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [query])

  return query
}
